import { Ref } from "react";
import styles from "./styles/skeleton-row.module.css";

/** Shimmering placeholder `<tr>` shown while more rows are loading — pair
 * with `useInfiniteRows()`'s `isLoadingMore` and `<InfiniteScrollSentinel>`. */
export function SkeletonRow({ columns }: { columns: number }) {
  return (
    <tr className={styles.skeletonRow} aria-hidden="true">
      {Array.from({ length: columns }).map((_, index) => (
        <td key={index} className={styles.skeletonCell}>
          {/* Varied widths so the row doesn't read as one uniform gray
              block — mimics the ragged edge of real, varied-length content. */}
          <div
            className={styles.skeletonBar}
            style={{ width: `${55 + ((index * 17) % 35)}%` }}
          />
        </td>
      ))}
    </tr>
  );
}

/**
 * Invisible trigger row for `useInfiniteRows()` — render as the last row in
 * `<tbody>` (after real data, before/alongside any `<SkeletonRow>`s) only
 * while `hasMore` is true. Scrolling this into view of the table's scroll
 * container starts loading the next page.
 */
export function InfiniteScrollSentinel({
  sentinelRef,
  columns,
}: {
  /* `Ref<...>`, not `RefObject<...>` — `useInfiniteRows`'s `sentinelRef` is
     a callback ref (fires exactly when the sentinel mounts/unmounts, since
     it's conditionally rendered), not a plain ref object. `Ref<T>` is the
     union type that accepts both forms, matching whatever `ref` itself
     accepts. */
  sentinelRef: Ref<HTMLDivElement>;
  columns: number;
}) {
  return (
    <tr aria-hidden="true">
      <td colSpan={columns} className={styles.skeletonCell} style={{ padding: 0 }}>
        <div ref={sentinelRef} className={styles.sentinel} />
      </td>
    </tr>
  );
}
